import { useState, useEffect } from "react";
import { Plus, Trash2, Database, RefreshCw, Save, Shield, Users } from "lucide-react";
import { toast } from "sonner";
import API from "../../lib/api";

export default function AdminSettings() {
  const [allowlist, setAllowlist] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [settings, setSettings] = useState({ org_name: "", invited_count: 0 });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [alRes, setRes] = await Promise.all([
        API.get("/admin/allowlist"),
        API.get("/admin/settings")
      ]);
      setAllowlist(alRes.data.allowlist || []);
      setSettings(setRes.data);
    } catch {
      toast.error("Failed to load settings");
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const addEmail = async () => {
    if (!newEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      toast.error("Enter a valid email");
      return;
    }
    try {
      await API.post("/admin/allowlist", { email: newEmail.trim() });
      toast.success("Email added to allowlist");
      setNewEmail("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    }
  };

  const removeEmail = async (email) => {
    if (!window.confirm(`Remove ${email} from allowlist?`)) return;
    try {
      await API.delete(`/admin/allowlist/${email}`);
      toast.success("Removed from allowlist");
      fetchData();
    } catch {
      toast.error("Failed to remove");
    }
  };

  const saveSettings = async () => {
    try {
      await API.put("/admin/settings", settings);
      toast.success("Settings saved");
    } catch {
      toast.error("Failed to save");
    }
  };

  const seedData = async () => {
    try {
      await API.post("/admin/seed");
      toast.success("20 demo responses seeded!");
    } catch {
      toast.error("Failed to seed data");
    }
  };

  const clearSeed = async () => {
    if (!window.confirm("Clear all seed/demo responses?")) return;
    try {
      await API.delete("/admin/seed");
      toast.success("Seed data cleared");
    } catch {
      toast.error("Failed to clear");
    }
  };

  const clearAll = async () => {
    if (!window.confirm("DELETE ALL responses? This cannot be undone.")) return;
    try {
      await API.delete("/admin/responses-bulk");
      toast.success("All responses deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin w-8 h-8 border-4 border-[#a21caf] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <h1 className="font-parkinsans text-2xl sm:text-3xl">Settings</h1>

      {/* Admin Allowlist */}
      <div className="nb-card" data-testid="allowlist-section">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-[#a21caf]" strokeWidth={2.5} />
          <h2 className="font-parkinsans text-xl">Admin Allowlist</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">Only emails in this list (plus the super admin) can register as admin users.</p>

        <div className="flex gap-2 mb-4">
          <input
            type="email"
            placeholder="email@example.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className="nb-input flex-1 text-sm"
            data-testid="allowlist-email-input"
            onKeyDown={(e) => e.key === "Enter" && addEmail()}
          />
          <button onClick={addEmail} className="nb-btn-primary text-sm py-2 px-4" data-testid="add-email-btn">
            <Plus className="w-4 h-4" strokeWidth={2.5} /> Add
          </button>
        </div>

        {allowlist.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center">No emails in allowlist yet. Only the super admin can log in.</p>
        ) : (
          <div className="space-y-2">
            {allowlist.map((item) => (
              <div key={item.email} className="flex items-center justify-between px-4 py-2 rounded-xl border-2 border-[#e5e5e5] bg-[#fdf7ff]" data-testid={`allowlist-item-${item.email}`}>
                <div>
                  <p className="font-medium text-sm">{item.email}</p>
                  <p className="text-xs text-gray-400">Added by {item.added_by} on {new Date(item.created_at).toLocaleDateString()}</p>
                </div>
                <button
                  onClick={() => removeEmail(item.email)}
                  className="p-1.5 rounded-lg border-2 border-[#111] bg-white hover:bg-red-50 text-red-500 transition-colors shadow-[2px_2px_0px_0px_rgba(17,17,17,1)]"
                  data-testid={`remove-email-btn-${item.email}`}
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={2.5} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* App Settings */}
      <div className="nb-card" data-testid="app-settings-section">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-[#a21caf]" strokeWidth={2.5} />
          <h2 className="font-parkinsans text-xl">App Settings</h2>
        </div>
        <div className="grid sm:grid-cols-1 gap-4 mb-4">
          <div>
            <label className="block text-sm font-semibold mb-1.5">Organization Name</label>
            <input
              type="text"
              value={settings.org_name || ""}
              onChange={(e) => setSettings(p => ({ ...p, org_name: e.target.value }))}
              className="nb-input text-sm"
              data-testid="org-name-input"
            />
          </div>
        </div>
        <button onClick={saveSettings} className="nb-btn-primary text-sm py-2 px-4" data-testid="save-settings-btn">
          <Save className="w-4 h-4" strokeWidth={2.5} /> Save Settings
        </button>
      </div>

      {/* Data Management */}
      <div className="nb-card" data-testid="data-management-section">
        <div className="flex items-center gap-2 mb-4">
          <Database className="w-5 h-5 text-[#a21caf]" strokeWidth={2.5} />
          <h2 className="font-parkinsans text-xl">Data Management</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">Manage demo/seed data for testing the dashboard.</p>
        <div className="flex flex-wrap gap-3">
          <button onClick={seedData} className="nb-btn-secondary text-sm py-2 px-4" data-testid="seed-data-btn">
            <RefreshCw className="w-4 h-4" strokeWidth={2.5} /> Seed 20 Demo Responses
          </button>
          <button onClick={clearSeed} className="nb-btn-secondary text-sm py-2 px-4" data-testid="clear-seed-btn">
            <Trash2 className="w-4 h-4" strokeWidth={2.5} /> Clear Seed Data
          </button>
          <button onClick={clearAll} className="nb-btn-danger text-sm py-2 px-4" data-testid="clear-all-btn">
            <Trash2 className="w-4 h-4" strokeWidth={2.5} /> Delete All Responses
          </button>
        </div>
      </div>
    </div>
  );
}
